import { makeObservable, observable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import { AttachedFile } from './AttachedFile'
import { Block } from './Block'

export const DEFAULT_BACKGROUND_COLOR = '#020b1deb'
const DEFAULT_FONT_SIZE = '3rem'
const DEFAULT_FONT_WEIGHT = '600'

export const FONT_SIZES = {
  SMALL: '2rem',
  MEDIUM: '3rem',
  LARGE: '5rem',
} as const

export const FONT_WEIGHTS = {
  NORMAL: '400',
  BOLD: '600',
} as const

export const FULLSCREEN_SCALE: Record<string, string> = {
  [FONT_SIZES.SMALL]: '3.5rem',
  [FONT_SIZES.MEDIUM]: '5rem',
  [FONT_SIZES.LARGE]: '8rem',
}

export type ContentModuleConfig = {
  uuid?: string
  title?: string
  description?: string | null
  thumbnail?: AttachedFile
  backgroundColor?: string
  fontSize?: string
  fontWeight?: string
  blocks?: Block[]
}

export class ContentModule {
  title: string

  description: string | null | undefined = ''

  uuid: string

  blocks: Block[] = []

  thumbnail?: AttachedFile

  backgroundColor: string

  fontSize: string

  fontWeight: string

  constructor({
    uuid,
    title = '',
    description = '',
    thumbnail,
    backgroundColor = DEFAULT_BACKGROUND_COLOR,
    fontSize = DEFAULT_FONT_SIZE,
    fontWeight = DEFAULT_FONT_WEIGHT,
    blocks = [],
  }: ContentModuleConfig = {}) {
    this.uuid = uuid || uuidv4()
    this.title = title
    this.description = description
    this.thumbnail = thumbnail
    this.backgroundColor = backgroundColor
    this.fontSize = fontSize
    this.fontWeight = fontWeight
    this.blocks = blocks

    makeObservable(this, {
      title: observable,
      description: observable,
      uuid: observable,
      blocks: observable,
      thumbnail: observable,
      backgroundColor: observable,
      fontSize: observable,
      fontWeight: observable,
    })
  }

  clone(): ContentModule {
    return new ContentModule({
      uuid: this.uuid,
      title: this.title,
      description: this.description,
      thumbnail: this.thumbnail,
      backgroundColor: this.backgroundColor,
      fontSize: this.fontSize,
      fontWeight: this.fontWeight,
      blocks: this.blocks,
    })
  }

  addBlock(block: Block): void {
    this.blocks.push(block)
  }

  deleteBlock(uuid: string): void {
    this.blocks = this.blocks.filter((block) => block.uuid !== uuid)
  }

  getBlockByUuid(uuid: string): Block | undefined {
    return this.blocks.find((block) => block.uuid === uuid)
  }

  updateBlock(uuid: string, updatedBlock: Block): void {
    const blockIndex = this.blocks.findIndex((block) => block.uuid === uuid)
    if (blockIndex !== -1) {
      this.blocks[blockIndex] = updatedBlock
    }
  }

  validate(): Record<string, string> {
    const errors: Record<string, string> = {}

    if (!this.title.trim()) {
      errors.title = i18n.t('trainings.content.validation.title_required')
    } else if (this.title.length > 255) {
      errors.title = i18n.t('trainings.content.validation.title_maxlength')
    }
    return errors
  }
}
